import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card } from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";
import KPIDetailRenderer from "../components/KPIDetailRenderer";

const CLASSIFICAZIONE_LABELS = {
  100000000: "A - Eccellente",
  100000001: "B - Adeguato",
  100000002: "C - Critico",
  100000003: "D - Inadeguato",
};

const CLASSIFICAZIONE_COLORS = {
  100000000: {
    bg: "bg-green-100",
    text: "text-green-800",
    border: "border-green-400",
  },
  100000001: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-400",
  },
  100000002: {
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-400",
  },
  100000003: {
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-400",
  },
};

const STATO_LABELS = {
  100000000: "Bozza",
  100000001: "Completata",
  100000002: "Approvata",
};

const MACROCATEGORIA_LABELS = {
  100000000: "Manutenzione",
  100000001: "Servizi",
  100000002: "Forniture",
  100000003: "Consulenza",
};

function DettaglioValutazionePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiRequest, user, getAccessToken } = useAuth();
  const { showToast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Stato locale dei punteggi editabili
  const [editedScores, setEditedScores] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const [expandedNote, setExpandedNote] = useState(null);
  const [noteGenerali, setNoteGenerali] = useState("");
  const [azioniRichieste, setAzioniRichieste] = useState("");

  const isAdmin = user?.ruolo === 100000000;
  const canEdit = data?.phc_stato === 100000000; // Solo bozza
  const canComplete = canEdit;
  const canApprove = isAdmin && data?.phc_stato === 100000001;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiRequest(`/valutazioni-fornitori/${id}`);
      setData(result);
      setNoteGenerali(result.phc_note || "");
      setAzioniRichieste(result.phc_azionirichieste || "");

      // Inizializza punteggi editabili
      const scores = {};
      const notes = {};
      for (const d of result.dettagli || []) {
        scores[d.phc_dettagliovalutazioneid] =
          d.phc_punteggio != null ? String(d.phc_punteggio) : "";
        notes[d.phc_dettagliovalutazioneid] = d.phc_note || "";
      }
      setEditedScores(scores);
      setEditedNotes(notes);
    } catch (error) {
      console.error("Error loading valutazione:", error);
      showToast("Errore nel caricamento", "error");
      navigate("/valutazione-fornitori");
    } finally {
      setLoading(false);
    }
  }, [id, apiRequest, navigate, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calcolo punteggio live
  const liveScore = (() => {
    if (!data?.dettagli) return null;
    let totalWeight = 0;
    let weightedSum = 0;

    for (const d of data.dettagli) {
      const peso = d.criterio?.phc_peso || 1;
      const val = parseFloat(editedScores[d.phc_dettagliovalutazioneid]);
      if (!isNaN(val)) {
        weightedSum += val * peso;
        totalWeight += peso;
      }
    }

    return totalWeight > 0
      ? Math.round((weightedSum / totalWeight) * 100) / 100
      : null;
  })();

  // Contatore criteri compilati
  const criteriStats = (() => {
    if (!data?.dettagli)
      return { total: 0, scored: 0, manual: 0, manualScored: 0 };
    let total = 0;
    let scored = 0;
    let manual = 0;
    let manualScored = 0;
    for (const d of data.dettagli) {
      total++;
      const detId = d.phc_dettagliovalutazioneid;
      const val = d.phc_calcolatoautomaticamente
        ? d.phc_punteggio
        : parseFloat(editedScores[detId]);
      if (val != null && !isNaN(val)) scored++;
      if (!d.phc_calcolatoautomaticamente) {
        manual++;
        if (editedScores[detId] !== "" && editedScores[detId] !== undefined)
          manualScored++;
      }
    }
    return { total, scored, manual, manualScored };
  })();

  const liveClassificazione = (() => {
    if (liveScore == null) return null;
    if (liveScore >= 80) return 100000000;
    if (liveScore >= 60) return 100000001;
    if (liveScore >= 40) return 100000002;
    return 100000003;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const dettagli = (data.dettagli || [])
        .filter((d) => !d.phc_calcolatoautomaticamente)
        .map((d) => ({
          id: d.phc_dettagliovalutazioneid,
          punteggio:
            editedScores[d.phc_dettagliovalutazioneid] !== ""
              ? parseFloat(editedScores[d.phc_dettagliovalutazioneid])
              : null,
          note: editedNotes[d.phc_dettagliovalutazioneid] || null,
        }));

      const result = await apiRequest(`/valutazioni-fornitori/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          dettagli,
          note: noteGenerali,
          azionirichieste: azioniRichieste,
        }),
      });

      showToast(
        `Salvato - Punteggio: ${result.punteggioFinale} (${result.classificazioneLabel})`,
        "success",
      );
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const [recalculating, setRecalculating] = useState(false);

  const handleRecalcKPI = async () => {
    setRecalculating(true);
    try {
      await apiRequest(`/valutazioni-fornitori/${id}/ricalcola`, {
        method: "PUT",
      });
      showToast("KPI ricalcolati", "success");
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nel ricalcolo", "error");
    } finally {
      setRecalculating(false);
    }
  };

  const handleCompleta = async () => {
    try {
      await apiRequest(`/valutazioni-fornitori/${id}/completa`, {
        method: "PUT",
      });
      showToast("Valutazione completata", "success");
      setConfirmAction(null);
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nel completamento", "error");
    }
  };

  const handleApprova = async () => {
    try {
      await apiRequest(`/valutazioni-fornitori/${id}/approva`, {
        method: "PUT",
      });
      showToast("Valutazione approvata", "success");
      setConfirmAction(null);
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nell'approvazione", "error");
    }
  };

  const handleExportPdf = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`/api/valutazioni-fornitori/${id}/pdf`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Errore download PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Valutazione_${data?.fornitore?.phc_name || "Fornitore"}_${data?.phc_periodo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("PDF scaricato", "success");
    } catch (error) {
      showToast("Errore nel download PDF", "error");
    }
  };

  const handleDelete = async () => {
    try {
      await apiRequest(`/valutazioni-fornitori/${id}`, { method: "DELETE" });
      showToast("Valutazione eliminata", "success");
      navigate("/valutazione-fornitori");
    } catch (error) {
      showToast(error.message || "Errore nell'eliminazione", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const classColors = CLASSIFICAZIONE_COLORS[liveClassificazione] || {
    bg: "bg-gray-100",
    text: "text-gray-600",
    border: "border-gray-300",
  };

  const formatDate = (d) => {
    if (!d) return "N/D";
    return new Date(d).toLocaleDateString("it-IT");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate("/valutazione-fornitori")}
            className="text-sm text-blue-600 hover:underline mb-2 inline-block"
          >
            &larr; Torna alla lista
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{data.phc_name}</h1>
          <div className="flex gap-3 mt-2">
            <span
              className={`text-xs px-2 py-1 rounded ${
                data.phc_stato === 100000000
                  ? "bg-gray-100 text-gray-700"
                  : data.phc_stato === 100000001
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
              }`}
            >
              {STATO_LABELS[data.phc_stato]}
            </span>
            <span className="text-sm text-gray-500">
              {MACROCATEGORIA_LABELS[data.phc_macrocategoria]}
            </span>
            <span className="text-sm text-gray-500">
              {formatDate(data.phc_datainizio)} -{" "}
              {formatDate(data.phc_datafine)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPdf}>
            PDF
          </Button>
          {canEdit && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          )}
          {canComplete && (
            <Button
              variant="outline"
              onClick={() => setConfirmAction("completa")}
            >
              Completa
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => setConfirmAction("approva")}>Approva</Button>
          )}
          {isAdmin && canEdit && (
            <Button
              variant="danger"
              onClick={() => setConfirmAction("elimina")}
            >
              Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Fornitore Info */}
      {data.fornitore && (
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Ragione Sociale</p>
              <p className="font-medium">{data.fornitore.phc_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">P.IVA</p>
              <p className="font-mono text-sm">
                {data.fornitore.phc_partitaiva}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm">{data.fornitore.phc_emailprincipale}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Periodo</p>
              <p className="font-medium">{data.phc_periodo}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Score Box */}
      <Card className={`p-6 border-2 ${classColors.border}`}>
        <div className="flex items-center gap-8">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Punteggio Finale</p>
            <p className={`text-4xl font-bold ${classColors.text}`}>
              {liveScore != null ? liveScore : "-"}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Classificazione</p>
            <span
              className={`text-xl font-bold px-4 py-2 rounded ${classColors.bg} ${classColors.text}`}
            >
              {liveClassificazione != null
                ? CLASSIFICAZIONE_LABELS[liveClassificazione]
                : "-"}
            </span>
          </div>
          <div className="ml-auto text-right text-xs text-gray-400">
            <div>A: 80-100 Eccellente</div>
            <div>B: 60-79 Adeguato</div>
            <div>C: 40-59 Critico</div>
            <div>D: 0-39 Inadeguato</div>
          </div>
        </div>
      </Card>

      {/* Criteri Table */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Dettaglio Criteri
            </h2>
            <div className="flex gap-2 text-xs">
              <span
                className={`px-2 py-1 rounded ${
                  criteriStats.scored === criteriStats.total
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {criteriStats.scored}/{criteriStats.total} compilati
              </span>
              {canEdit && criteriStats.manual > 0 && (
                <span
                  className={`px-2 py-1 rounded ${
                    criteriStats.manualScored === criteriStats.manual
                      ? "bg-green-100 text-green-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {criteriStats.manualScored}/{criteriStats.manual} manuali
                </span>
              )}
            </div>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecalcKPI}
              disabled={recalculating}
            >
              {recalculating ? "Ricalcolo..." : "Ricalcola KPI"}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  Criterio
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-20">
                  Tipo
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-16">
                  Peso
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-32">
                  Punteggio (0-100)
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  Note / Evidenze
                </th>
              </tr>
            </thead>
            <tbody>
              {(data.dettagli || []).map((d) => {
                const isAuto = d.phc_calcolatoautomaticamente;
                const detId = d.phc_dettagliovalutazioneid;
                let datiAuto = null;
                if (d.phc_datiautomatici) {
                  try {
                    datiAuto = JSON.parse(d.phc_datiautomatici);
                  } catch {
                    // ignore
                  }
                }

                const hasScore = isAuto
                  ? d.phc_punteggio != null
                  : editedScores[detId] !== "" &&
                    editedScores[detId] !== undefined;
                const isMissingManual = canEdit && !isAuto && !hasScore;

                return (
                  <tr
                    key={detId}
                    className={`border-b hover:bg-gray-50 ${isMissingManual ? "bg-amber-50/50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{d.phc_name}</div>
                        {isMissingManual && (
                          <span
                            className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"
                            title="Da compilare"
                          />
                        )}
                      </div>
                      {d.criterio?.phc_descrizione && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {d.criterio.phc_descrizione}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          isAuto
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {isAuto ? "AUTO" : "MAN"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-mono">
                      {d.criterio?.phc_peso || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isAuto ? (
                        <div>
                          <span className="font-bold text-blue-700">
                            {d.phc_punteggio != null ? d.phc_punteggio : "-"}
                          </span>
                          {datiAuto && (
                            <details className="mt-1">
                              <summary className="text-xs text-blue-500 cursor-pointer">
                                dettagli
                              </summary>
                              <div className="mt-1 bg-gray-50 p-2 rounded text-left">
                                <KPIDetailRenderer
                                  formula={d.criterio?.phc_formulaautomatica}
                                  dati={datiAuto}
                                />
                              </div>
                            </details>
                          )}
                        </div>
                      ) : canEdit ? (
                        <div className="flex items-center gap-2">
                          {(() => {
                            const scoreVal = editedScores[detId];
                            const isEmpty =
                              scoreVal === "" || scoreVal === undefined;
                            const numVal = isEmpty ? 0 : parseInt(scoreVal);
                            const color = isEmpty
                              ? "#d1d5db"
                              : numVal >= 80
                                ? "#16a34a"
                                : numVal >= 60
                                  ? "#2563eb"
                                  : numVal >= 40
                                    ? "#ea580c"
                                    : "#dc2626";
                            const fill = isEmpty ? 0 : numVal;
                            return (
                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={isEmpty ? 50 : numVal}
                                onChange={(e) =>
                                  setEditedScores({
                                    ...editedScores,
                                    [detId]: e.target.value,
                                  })
                                }
                                className={`w-24 h-2 cursor-pointer ${isEmpty ? "opacity-30" : ""}`}
                                style={{
                                  accentColor: color,
                                  background: `linear-gradient(to right, ${color} ${fill}%, #e5e7eb ${fill}%)`,
                                }}
                              />
                            );
                          })()}
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={editedScores[detId] || ""}
                            onChange={(e) =>
                              setEditedScores({
                                ...editedScores,
                                [detId]: e.target.value,
                              })
                            }
                            className="w-14 text-center rounded border-gray-300 shadow-sm text-xs focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      ) : (
                        <span className="font-bold">
                          {d.phc_punteggio != null ? d.phc_punteggio : "-"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {canEdit && !isAuto ? (
                        expandedNote === detId ? (
                          <div className="relative">
                            <textarea
                              value={editedNotes[detId] || ""}
                              onChange={(e) =>
                                setEditedNotes({
                                  ...editedNotes,
                                  [detId]: e.target.value,
                                })
                              }
                              rows={4}
                              placeholder="Note, evidenze, giustificazione del punteggio..."
                              className="w-full rounded border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              onClick={() => setExpandedNote(null)}
                              className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-xs bg-white px-1 rounded"
                            >
                              chiudi
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => setExpandedNote(detId)}
                            className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm text-gray-500 cursor-pointer hover:border-blue-300 hover:bg-blue-50 min-h-[32px] truncate"
                            title="Clicca per espandere"
                          >
                            {editedNotes[detId] || (
                              <span className="italic text-gray-300">
                                Clicca per aggiungere note...
                              </span>
                            )}
                          </div>
                        )
                      ) : (
                        <span className="text-sm text-gray-600">
                          {d.phc_note || "-"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Note e Azioni */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Note Generali</h3>
          {canEdit ? (
            <textarea
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              rows={4}
              value={noteGenerali}
              onChange={(e) => setNoteGenerali(e.target.value)}
              placeholder="Note sulla valutazione..."
            />
          ) : (
            <p className="text-sm text-gray-600">
              {data.phc_note || "Nessuna nota"}
            </p>
          )}
        </Card>

        <Card
          className={`p-4 ${
            liveClassificazione != null && liveClassificazione >= 100000002
              ? "border-amber-300 bg-amber-50"
              : ""
          }`}
        >
          <h3 className="font-semibold text-gray-900 mb-2">
            Azioni Richieste
            {liveClassificazione != null &&
              liveClassificazione >= 100000002 && (
                <span className="text-xs text-amber-600 ml-2">
                  (obbligatorio per classe C/D)
                </span>
              )}
          </h3>
          {canEdit ? (
            <textarea
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              rows={4}
              value={azioniRichieste}
              onChange={(e) => setAzioniRichieste(e.target.value)}
              placeholder="Piano di miglioramento, azioni correttive..."
            />
          ) : (
            <p className="text-sm text-gray-600">
              {data.phc_azionirichieste || "Nessuna azione richiesta"}
            </p>
          )}
        </Card>
      </div>

      {/* Confirm Modals */}
      <ConfirmModal
        isOpen={confirmAction === "completa"}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleCompleta}
        title="Completa Valutazione"
        message="Una volta completata, la valutazione non potrà più essere modificata. Vuoi procedere?"
        confirmText="Completa"
      />
      <ConfirmModal
        isOpen={confirmAction === "approva"}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleApprova}
        title="Approva Valutazione"
        message="Confermi l'approvazione di questa valutazione?"
        confirmText="Approva"
      />
      <ConfirmModal
        isOpen={confirmAction === "elimina"}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleDelete}
        title="Elimina Valutazione"
        message="Sei sicuro di voler eliminare questa valutazione? L'operazione non è reversibile."
        confirmText="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default DettaglioValutazionePage;
